import { Link, useLocation } from "wouter";
import { Star, Check, Plus, Crown, ShieldCheck, ExternalLink, ChevronRight } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LOGO_IMAGE_WIDTH, sizedUploadUrl } from "@/lib/imageUrl";

interface BrokerCardProps {
  id: string;
  name: string;
  rating: number;
  reviewsCount?: number;
  logoColor?: string;
  logoUrl?: string | null;
  minDeposit: string | number;
  maxLeverage: string | number;
  spread: string | number;
  regulator: string;
  bonus?: string;
  featured?: boolean;
  isInspiredLayout?: boolean;
  accountType?: string;
  isSpotlight?: boolean;
  openAccountUrl?: string | null;
  listBadgeText?: string | null;
  wellPerforming?: boolean;
  topRank?: number;
  onCompareToggle?: (id: string, selected: boolean) => void;
  isCompareSelected?: boolean;
  compareCount?: number;
}

export function BrokerCard({ 
  id,
  name, 
  rating, 
  reviewsCount = 0,
  logoColor = "bg-blue-600", 
  logoUrl,
  minDeposit, 
  maxLeverage, 
  spread,
  regulator,
  bonus,
  featured,
  isInspiredLayout,
  accountType = "Standard Account",
  isSpotlight,
  openAccountUrl,
  listBadgeText,
  wellPerforming,
  topRank,
  onCompareToggle,
  isCompareSelected,
  compareCount = 0
}: BrokerCardProps) {
  const [, setLocation] = useLocation();
  const [logoOk, setLogoOk] = useState(true);
  const showLogoImage = !!logoUrl && logoOk;
  const spotlightBadgeText = String(listBadgeText || "").trim() || (isSpotlight ? "Institutional Elite Choice" : "");
  const showWellPerformingBadge = wellPerforming !== false;
  const topRankValue = Number(topRank || 0);
  const showTopRankBadge = topRankValue >= 1 && topRankValue <= 10;
  const rankBadge = (() => {
    const rank = topRankValue;
    if (rank === 1) return { cls: "from-primary to-yellow-400 border-primary/50 text-black shadow-[0_0_18px_rgba(234,179,8,0.35)]" };
    if (rank === 2) return { cls: "from-slate-200 to-slate-400 border-slate-200/50 text-[#111827] shadow-[0_0_18px_rgba(203,213,225,0.25)]" };
    if (rank === 3) return { cls: "from-orange-500 to-amber-400 border-orange-400/50 text-black shadow-[0_0_18px_rgba(249,115,22,0.28)]" };
    if (rank >= 4 && rank <= 6) return { cls: "from-blue-500 to-cyan-400 border-blue-400/50 text-white shadow-[0_0_18px_rgba(59,130,246,0.22)]" };
    return { cls: "from-violet-500 to-fuchsia-400 border-violet-400/50 text-white shadow-[0_0_18px_rgba(168,85,247,0.22)]" };
  })();
  const leverageDisplay = (() => {
    const raw = String(maxLeverage ?? "").trim();
    if (!raw) return "—";
    if (/^1\s*:/i.test(raw)) return raw.replace(/^1\s*:\s*/i, "1:");
    if (/^unlimited$/i.test(raw)) return "1:Unlimited";
    if (/^\d+(?:\.\d+)?$/.test(raw)) return `1:${raw}`;
    return raw;
  })();

  const ratingSafe = Number.isFinite(Number(rating))
    ? Math.max(0, Math.min(5, Number(rating)))
    : 0;

  const renderRatingStars = (sizeClass: string) => {
    // role="img" makes the aria-label legal. A bare <div> has a generic role and
    // ARIA forbids naming a generic element — PageSpeed's aria-prohibited-attr
    // audit flagged this node for exactly that. A star rating is a picture of a
    // score, so role="img" plus the label is the intended pattern.
    return (
      <div
        role="img"
        className="flex items-center gap-1"
        aria-label={`Rating ${ratingSafe.toFixed(1)} out of 5`}
      >
        {[...Array(5)].map((_, i) => {
          const fill = Math.max(0, Math.min(1, ratingSafe - i));
          const pct = `${Math.round(fill * 100)}%`;
          return (
            <span key={i} className={"relative inline-block " + sizeClass}>
              <Star className={"absolute inset-0 " + sizeClass + " text-white/10 fill-white/10"} />
              <span className="absolute inset-0 overflow-hidden" style={{ width: pct }}>
                <Star className={sizeClass + " text-primary fill-primary"} />
              </span>
            </span>
          );
        })}
      </div>
    );
  };

  const handleCardClick = () => {
    setLocation(`/broker/${id}`);
  };

  const handleOpenAccount = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = (openAccountUrl || "").toString().trim();
    window.open(url || "https://www.xm.com/register", "_blank");
  };

  if (isInspiredLayout) {
    return (
      <Card 
        className={`overflow-relative group transition-all duration-500 cursor-pointer ${
          isSpotlight 
            ? 'mt-4 border-0 bg-transparent p-[2px] animate-spotlight-pulse md:mt-0' 
            : 'mt-4 border-white/5 bg-[#161e2e] hover:border-primary/30 md:mt-0'
        }`}
        onClick={handleCardClick}
      >
        <CardContent className="p-0 relative h-full">
          {showTopRankBadge ? (
            <div className="absolute left-4 top-0 z-30 -translate-y-1/2 md:hidden">
              <div className={`inline-flex h-8 items-center gap-1.5 rounded-full border bg-gradient-to-r px-3 text-[10px] font-black uppercase tracking-wider ${rankBadge.cls}`}>
                <Crown className="h-3.5 w-3.5 fill-black/20" />
                #{topRankValue}
              </div>
            </div>
          ) : null}

          {showTopRankBadge ? (
            <div className="absolute left-4 top-0 z-30 hidden -translate-y-1/2 md:block">
              <div className={`inline-flex h-8 items-center gap-1.5 rounded-full border bg-gradient-to-r px-3 text-[10px] font-black uppercase tracking-wider ${rankBadge.cls}`}>
                <Crown className="h-3.5 w-3.5 fill-black/20" />
                #{topRankValue}
              </div>
            </div>
          ) : null}

          {(isSpotlight || spotlightBadgeText) && (
            <>
              {isSpotlight ? (
                <>
                  {/* Animated Gradient Border Frame */}
                  <div className="absolute -inset-[2px] rounded-[1.2rem] bg-gradient-to-r from-primary via-[#fbbf24] to-primary bg-[length:200%_auto] animate-gradient-x -z-10 opacity-0" />
                  
                  {/* Internal Glow Overlay */}
                  <div className="absolute inset-0 rounded-[1.1rem] bg-[#161e2e] -z-10" />
                  
                  {/* Scanline Effect */}
                  <div className="absolute inset-0 overflow-hidden rounded-[1.1rem] -z-5 pointer-events-none opacity-20">
                    <div className="w-full h-1/2 bg-gradient-to-b from-transparent via-primary/10 to-transparent animate-scanline" />
                  </div>
                  
                  {/* Decorative Corner Accents */}
                  <div className="absolute top-0 left-0 w-8 h-8 rounded-tl-[1rem] z-20 group-hover:scale-110 transition-transform" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 rounded-br-[1rem] z-20 group-hover:scale-110 transition-transform" />
                </>
              ) : null}

              {spotlightBadgeText ? (
                <div className="absolute -top-3 left-1/2 z-30 hidden -translate-x-1/2 md:block">
                  <div className="bg-primary text-black font-black text-[9px] uppercase tracking-[0.3em] px-6 py-1.5 rounded-full shadow-[0_0_20px_rgba(234,179,8,0.4)] border-2 border-black whitespace-nowrap animate-bounce-subtle">
                    {spotlightBadgeText}
                  </div>
                </div>
              ) : null}
            </>
          )}
          
          <div className="flex flex-col md:flex-row items-stretch relative bg-[#161e2e] rounded-[1.1rem] overflow-hidden h-full">
            {/* Logo & Action Column */}
            <div className="flex flex-col gap-4 border-b border-white/5 bg-black/10 p-5 md:w-1/4 md:items-center md:justify-center md:border-b-0 md:border-r md:p-6">
              <div className="flex items-start gap-4 md:block">
                <div className={`h-16 w-16 shrink-0 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform overflow-hidden md:h-20 md:w-20 ${showLogoImage ? "border-transparent bg-transparent shadow-none" : `${logoColor} bg-white/10 shadow-2xl`}`}>
                  {showLogoImage ? (
                    <img
                      src={sizedUploadUrl(logoUrl, LOGO_IMAGE_WIDTH)}
                      alt={name}
                      className="w-full h-full object-contain p-2"
                      onError={() => setLogoOk(false)}
                    />
                  ) : (
                    <div className="text-white font-black text-xl md:text-2xl">{name === "XM Global" ? "XM" : name.substring(0, 2)}</div>
                  )}
                </div>

                <div className="min-w-0 flex-1 pt-0.5 md:hidden">
                  <h3 className="truncate text-lg font-black uppercase tracking-tight text-white transition-colors group-hover:text-primary">
                    {name}
                  </h3>
                  <div className="mt-2 flex flex-col items-start gap-1">
                    <div className="flex items-center gap-2">
                      {renderRatingStars("w-3 h-3")}
                      <span className="font-black text-xs text-white">{ratingSafe.toFixed(1)}</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{reviewsCount} Reviews</span>
                  </div>
                  <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                    <span>Regulators: </span>
                    <span className="text-gray-300">{regulator}</span>
                  </div>
                </div>
              </div>
              <Button 
                className="w-full bg-white/5 hover:bg-primary hover:text-black text-white text-[10px] font-black uppercase tracking-widest h-10 rounded-lg transition-all border border-white/10"
                onClick={handleOpenAccount}
              >
                Open Account
              </Button>
            </div>

            {/* Info Column */}
            <div className={`${onCompareToggle ? "border-b border-white/5 bg-gradient-to-r from-white/[0.035] to-transparent p-5" : "hidden p-0"} md:block md:flex-grow md:space-y-4 md:border-b-0 md:bg-transparent md:p-6`}>
              <div className="hidden flex-wrap items-center gap-3 md:flex">
                <h3 className="text-xl font-black text-white hover:text-primary transition-colors tracking-tight uppercase">
                  {name}
                </h3>
                {showWellPerformingBadge ? (
                  <Badge className="bg-orange-500/10 text-orange-500 border-none text-[9px] font-black uppercase tracking-widest h-5">
                    WELL-PERFORMING BROKER
                  </Badge>
                ) : null}
              </div>

              <div className="hidden flex-col items-start gap-1 md:flex">
                <div className="flex items-center gap-2">
                  {renderRatingStars("w-3 h-3")}
                  <span className="font-black text-xs text-white">{ratingSafe.toFixed(1)}</span>
                </div>
                <span className="text-gray-500 text-[10px] font-bold uppercase tracking-widest">{reviewsCount} Reviews</span>
              </div>

              <div className="hidden text-[10px] text-gray-500 font-bold uppercase tracking-widest md:flex items-center gap-2">
                <span>Regulators:</span>
                <span className="text-gray-300">{regulator}</span>
              </div>

              {/* Compare used to be a bare 12px checkbox ("w-3 h-3") next to 9px grey
                  text, which was easy to miss entirely. It is now a full-size pill
                  button with an icon and three clearly distinct states. */}
              {onCompareToggle ? (() => {
                const compareFull = compareCount >= 3 && !isCompareSelected;
                return (
                  <button
                    type="button"
                    aria-pressed={!!isCompareSelected}
                    disabled={compareFull}
                    title={compareFull ? "You can compare up to 3 brokers — deselect one first" : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCompareToggle(id, !isCompareSelected);
                    }}
                    className={
                      "inline-flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-3 text-[10px] font-black uppercase tracking-[0.18em] transition-all md:w-auto md:justify-start md:rounded-full md:px-3 md:py-2 md:tracking-widest " +
                      (isCompareSelected
                        ? "border-primary bg-primary text-black shadow-[0_12px_28px_rgba(234,179,8,0.22)] md:bg-primary/15 md:text-primary md:shadow-[0_0_22px_rgba(234,179,8,0.18)]"
                        : compareFull
                          ? "border-white/5 bg-white/5 text-gray-600 cursor-not-allowed"
                          : "border-primary/35 bg-gradient-to-r from-primary/15 via-white/[0.07] to-white/[0.03] text-white shadow-[0_12px_28px_rgba(0,0,0,0.22)] hover:border-primary/60 hover:bg-primary/10 hover:text-primary md:border-white/15 md:bg-white/5 md:bg-none md:text-gray-400 md:shadow-none")
                    }
                  >
                    {isCompareSelected ? (
                      <Check className="w-3.5 h-3.5 shrink-0" />
                    ) : (
                      <Plus className="w-3.5 h-3.5 shrink-0" />
                    )}
                    {isCompareSelected ? "Added to Compare" : "Add to Compare"}
                  </button>
                );
              })() : null}
            </div>

            {/* Account Stats Column */}
            <div className="p-6 md:w-1/3 bg-black/20 flex flex-col justify-between group/account hover:bg-black/40 transition-colors">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white text-xs font-black uppercase tracking-tighter">{accountType}</span>
                  <ChevronRight className="w-4 h-4 text-gray-600 group-hover/account:text-primary transition-colors" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between text-[10px] uppercase tracking-widest">
                    <span className="text-gray-500 font-bold">Min Deposit</span>
                    <span className="text-gray-300 font-black">{minDeposit} USD</span>
                  </div>
                  <div className="flex justify-between text-[10px] uppercase tracking-widest">
                    <span className="text-gray-500 font-bold">Max Lev</span>
                    <span className="text-gray-300 font-black">{leverageDisplay}</span>
                  </div>
                  <div className="flex justify-between text-[10px] uppercase tracking-widest">
                    <span className="text-gray-500 font-bold">Avg Spread</span>
                    <span className="text-gray-300 font-black">{spread}</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4">
                <span className="text-[9px] text-primary font-black uppercase tracking-widest hover:underline">Select your account {'>'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fallback to original layout - Dark Theme
  return (
    <Card 
      className={`overflow-hidden transition-all hover:shadow-xl group cursor-pointer bg-[#161e2e] ${featured ? 'border-primary border-2 shadow-md' : 'border-white/5'}`}
      onClick={handleCardClick}
    >
      {featured && (
        <div className="bg-primary text-black text-xs font-bold text-center py-1 uppercase tracking-wide">
          Top Recommendation
        </div>
      )}
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row">
          {/* Logo Section */}
          <div className="p-6 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-white/5 md:w-1/4 bg-black/20">
            <div className={`w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-xl mb-3 group-hover:scale-110 transition-transform overflow-hidden ${showLogoImage ? "border-transparent bg-transparent shadow-none" : `${logoColor} bg-white/10 shadow-sm`}`}>
              {showLogoImage ? (
                <img
                  src={sizedUploadUrl(logoUrl, LOGO_IMAGE_WIDTH)}
                  alt={name}
                  className="w-full h-full object-contain p-2"
                  onError={() => setLogoOk(false)}
                />
              ) : (
                <>{name.substring(0, 2)}</>
              )}
            </div>
            <div className="flex items-center space-x-1 text-primary">
              {renderRatingStars("w-4 h-4")}
            </div>
            <span className="text-xs text-gray-400 mt-1 font-medium">{ratingSafe.toFixed(1)}/5.0</span>
          </div>

          {/* Details Section */}
          <div className="p-6 flex-grow grid grid-cols-2 gap-y-4 gap-x-8">
            <div className="col-span-2 md:col-span-1">
              <h3 className="font-heading font-bold text-xl text-white flex items-center gap-2 hover:text-primary transition-colors">
                {name}
                {regulator && <Badge variant="outline" className="text-[10px] h-5 px-1 font-normal text-gray-400 border-white/10"><ShieldCheck className="w-3 h-3 mr-1" /> {regulator}</Badge>}
              </h3>
              {bonus && <p className="text-sm text-green-500 font-bold mt-1">{bonus}</p>}
            </div>

            <div className="col-span-2 md:col-span-1 flex flex-col justify-center space-y-1">
               <div className="flex justify-between text-sm">
                 <span className="text-gray-500">Min Deposit:</span>
                 <span className="font-bold text-white">{minDeposit}</span>
               </div>
               <div className="flex justify-between text-sm">
                 <span className="text-gray-500">Max Leverage:</span>
                 <span className="font-bold text-white">{leverageDisplay}</span>
               </div>
               <div className="flex justify-between text-sm">
                 <span className="text-gray-500">Spread From:</span>
                 <span className="font-bold text-white">{spread}</span>
               </div>
            </div>
          </div>

          {/* Action Section */}
          <div className="p-6 flex flex-col justify-center gap-3 md:w-1/4 bg-black/20 border-t md:border-t-0 md:border-l border-white/5">
            <Button 
              className="w-full font-black bg-primary hover:bg-white text-black shadow-sm h-12"
              onClick={handleOpenAccount}
            >
              Open Account <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full text-xs font-bold text-gray-400 border-white/10 border-dashed h-10 hover:border-primary hover:text-primary transition-colors bg-transparent"
              onClick={handleCardClick}
            >
              Read Review
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
